import React from 'react';
import classnames from 'classnames';
import { useMutation } from '@apollo/client/react/hooks';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import { Formik, Form, Field, FieldInputProps } from 'formik';
import flatMap from 'lodash/flatMap';

import TextArea from 'four-nets/ui-v2/form/text-area/text-area';

import defaultStyles from './vendor-videos.scss';
import { VendorVideoLinkFragment } from './vendor-videos.graphql';
import { VendorVideoLinkType } from './vendor-video.graphql.types';
import mutation, { UpdateVendorVideosMutation, UpdateVendorVideosMutationVariables } from './mutation.graphql';

const vimeoRe = new RegExp('https://vimeo.com/([0-9]+)');
const youtubeRe = new RegExp('https://(?:www.)?youtu(?:be.com/watch\\?v=|.be/)([\\w-]+)');

export interface Props {
    styles: typeof defaultStyles;
    links: VendorVideoLinkFragment[];
}

interface VideoProps {
    styles: typeof defaultStyles;
    link: VendorVideoLinkFragment;
}

const VendorVideo: React.FC<VideoProps> = props => {
    const {
        styles,
        link: { videoCode, linkType },
    } = props;

    const [iframeLink, typeClassName] =
        linkType === VendorVideoLinkType.YOUTUBE
            ? [`https://www.youtube.com/embed/${videoCode}`, styles.youtubeWrapper]
            : [`https://player.vimeo.com/video/${videoCode}?title=0&byline=0&portrait=0`, ''];
    return (
        <div className={classnames([styles.iframeWrapper, typeClassName])}>
            <iframe src={iframeLink} frameBorder='0' allow='fullscreen' allowFullScreen={true} />
        </div>
    );
};

interface EditVendorVideosProps {
    styles: typeof defaultStyles;
    links: string[];
    onSubmit: (links: VendorVideoLinkFragment[]) => void;
}

interface EditVendorVideosValues {
    videoLinks: string;
}

type EditVendorVideosErrors = Partial<{ [key in keyof EditVendorVideosValues]: React.ReactNode[] }>;

const _EditVendorVideos: React.FC<EditVendorVideosProps> = props => {
    const { links, styles, onSubmit } = props;
    const [updateVendorVideos] = useMutation<UpdateVendorVideosMutation, UpdateVendorVideosMutationVariables>(mutation);

    return (
        <Formik<EditVendorVideosValues>
            initialValues={{
                videoLinks: links.join('\n'),
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validate={values => {
                const errors: EditVendorVideosErrors = {};
                const items: string[] = flatMap(values.videoLinks.split('\n'), line => line.split(' '));
                const videoLinksErrors = items
                    .filter(item => item.length !== 0)
                    .map(item =>
                        item.match(youtubeRe) || item.match(vimeoRe) ? null : (
                            <FormattedMessage id='INVALID_VIDEO_URL' values={{ url: item }} />
                        )
                    )
                    .filter(item => item !== null) as React.ReactNode[];
                if (videoLinksErrors.length > 0) {
                    errors.videoLinks = videoLinksErrors;
                }
                return errors;
            }}
            onSubmit={values => {
                const inputLinks = flatMap(values.videoLinks.split('\n'), line => line.split(' ')).filter(
                    item => item.length > 0
                );
                return updateVendorVideos({
                    variables: {
                        input: {
                            inputLinks,
                        },
                    },
                }).then(result => {
                    if (result.data && result.data.updateVendorVideos) {
                        onSubmit(result.data.updateVendorVideos.videoLinks);
                    }
                });
            }}
        >
            {({ isSubmitting, touched, errors }) => (
                <Form>
                    <Field name='videoLinks'>
                        {({ field }: { field: FieldInputProps<string> }) => (
                            <TextArea emojis={false} {...field} errors={errors.videoLinks} />
                        )}
                    </Field>
                    <button className={styles.submitButton} disabled={!touched || isSubmitting} type='submit'>
                        <FormattedMessage id='SAVE' values={{ gender: 'v' }} />
                    </button>
                </Form>
            )}
        </Formik>
    );
};

export const EditVendorVideos = Styled(defaultStyles)(_EditVendorVideos);

const VendorVideos: React.FC<Props> = props => {
    const { styles, links } = props;
    return (
        <ul className={styles.videos}>
            {links.map((link, index) => (
                <li key={index}>
                    <VendorVideo styles={styles} link={link} />
                </li>
            ))}
        </ul>
    );
};

export default Styled(defaultStyles)(VendorVideos);
